import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
  getGridStringOperators,
  getGridSingleSelectOperators,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { labelTooltip } from "../../constants/inventory_tooltips";
import useInventory from "hooks/useInventory";
import { Link } from "react-router-dom";
import { Inventory } from "types/inventory";
import { Box, styled, LinearProgress } from "@mui/material";
import { WarehouseLocationLink } from "components/Locations/WarehouseLocationLink";
import useServerSideDataGridParams from "hooks/useServerSideDataGridParams";
import { deviceTypes } from "constants/device_types";
import EnrolledInventoriesDataGridActions from "./EnrolledInventoriesDataGridActions";
import { readableDate } from "utils/readableDate";

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
  height: "80vh",
  border: "none",
  backgroundColor: "background.paper",
  "& .MuiDataGrid-row:hover": {
    backgroundColor: theme.palette.mode == "dark" ? theme.palette.primary.dark : theme.palette.primary.light,
  },
}));

export default function EnrolledInventoriesDataGrid() {
  const defaultPageSize = 200;
  const useInventoriesState = useInventory(defaultPageSize);
  const { enrolledInventories, count, selectedInventoryIds, setSelectedInventoryIds, isLoading, selectedInventories } =
    useInventoriesState;

  const apiRef = useGridApiRef();

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer>
        {/* MUI X's React type declarations incorrectly require resize handlers on these toolbar buttons. */}
        {/* eslint-disable @typescript-eslint/no-explicit-any */}
        <GridToolbarColumnsButton {...({} as any)} />
        <GridToolbarFilterButton {...({} as any)} />
        <GridToolbarDensitySelector {...({} as any)} />
        {/* eslint-enable @typescript-eslint/no-explicit-any */}
      </GridToolbarContainer>
    );
  };

  const columns: GridColDef[] = [
    {
      field: "label",
      headerName: "Label",
      description: labelTooltip,
      width: 125,
      filterOperators: getGridStringOperators().filter((op) => op.value === "contains"),
      renderCell: (params: GridRenderCellParams<Inventory>) => {
        if (params.row.pickup) {
          return <Link to={`/pickups/${params.row.pickup.uuid}/inventories/${params.row.id}`}>{params.row.label}</Link>;
        } else {
          return <div>{params.row.label}</div>;
        }
      },
    },
    {
      field: "pickup.scheduled_at",
      headerName: "Pickup Date",
      sortable: false,
      filterable: false,
      width: 175,
      filterOperators: getGridStringOperators().filter((op) => op.value === "contains"),
      renderCell: (params: GridRenderCellParams<Inventory>) => {
        if (params.row.pickup) {
          return <div>{readableDate(params.row.pickup.scheduledAt)}</div>;
        }
      },
    },
    {
      field: "warehouses.name",
      headerName: "Warehouse",
      width: 125,
      filterOperators: getGridSingleSelectOperators().filter((op) => op.value === "is"),
      valueOptions: [
        { label: "NY", value: "New York" },
        { label: "SF", value: "San Francisco" },
        { label: "3PL", value: "3PL" },
      ],
      renderCell: (params: GridRenderCellParams<Inventory>) => {
        const warehouseAcronymMap = {
          "New York": "NY",
          "San Francisco": "SF",
          "3PL": "3PL",
        };
        const warehouseName: keyof typeof warehouseAcronymMap = params.row.warehouse ? params.row.warehouse.name : "";
        return warehouseAcronymMap[warehouseName] ? warehouseAcronymMap[warehouseName] : "";
      },
    },
    {
      field: "warehouseLocation",
      headerName: "Location",
      width: 250,
      filterOperators: getGridStringOperators().filter((op) => op.value === "contains"),
      editable: true,
      renderCell: ({ row }: GridRenderCellParams<Inventory>) => (
        <WarehouseLocationLink code={row.warehouseLocation} locationId={row.locationId} />
      ),
    },
    {
      field: "manufacturer",
      headerName: "Manufacturer",
      width: 150,
      filterOperators: getGridStringOperators().filter((op) => op.value === "contains"),
    },
    {
      field: "deviceType",
      headerName: "Device Type",
      width: 200,
      filterOperators: getGridSingleSelectOperators().filter((op) => op.value === "is"),
      valueOptions: deviceTypes,
    },
    {
      field: "serialNumber",
      headerName: "Serial Number",
      width: 250,
      filterOperators: getGridStringOperators().filter((op) => op.value === "contains"),
      editable: true,
    },
  ];

  const pageState = useServerSideDataGridParams(enrolledInventories);

  return (
    <Box sx={{ width: "100%", height: "80vh" }}>
      {selectedInventories.length ? (
        <EnrolledInventoriesDataGridActions useInventoriesState={useInventoriesState} />
      ) : null}

      <StyledDataGrid
        rows={enrolledInventories}
        columns={columns}
        checkboxSelection
        {...pageState}
        pageSize={pageState.pageSize || defaultPageSize}
        rowsPerPageOptions={[5, 10, 25, 100, 200]}
        rowCount={count}
        selectionModel={selectedInventoryIds}
        onSelectionModelChange={setSelectedInventoryIds}
        filterMode="server"
        apiRef={apiRef}
        loading={isLoading}
        components={{
          LoadingOverlay: LinearProgress,
          Toolbar: CustomToolbar,
        }}
        componentsProps={{ filterPanel: { sx: { minHeight: "200px" } } }}
      />
    </Box>
  );
}
